import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArticlesRequestError,
  fetchArticles,
  type ArticleItem,
} from '../api/articlesApi';

export type ArticlesLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: ArticleItem[] }
  | { status: 'error'; error: ArticlesRequestError };

export function useArticles() {
  const [state, setState] = useState<ArticlesLoadState>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ status: 'loading' });

    try {
      const data = await fetchArticles(controller.signal);
      if (!controller.signal.aborted) {
        setState({ status: 'success', data });
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      setState({
        status: 'error',
        error:
          error instanceof ArticlesRequestError
            ? error
            : new ArticlesRequestError(
                'unknown',
                'Something went wrong while loading articles.'
              ),
      });
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    const handleOnline = () => {
      if (state.status === 'error' && state.error.kind === 'network') {
        void load();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [load, state]);

  return { state, reload: load };
}
